import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import type { DispensingUsability, OpticalJobDetail } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { ClinicalEyeGrid } from '@/modules/medical/components/ClinicalEyeGrid';
import { PdSummary } from '../../steps/PrescriptionStep';
import { eyeFormOf } from '../../tracking/prescription';

interface JobPrescriptionCardProps {
  job: OpticalJobDetail;
  /** Where the clinical prescription it names stands today; null when not known or not clinical. */
  clinicalUsability: DispensingUsability | null;
  /** Offered only while the job is ORDERED and the user may correct it. */
  onCorrect?: () => void;
}

/**
 * The prescription the pair is made to, read-only.
 *
 * Values are the job's own copy, left to right with the right eye first in every language, the PD exactly as recorded. A clinical prescription that has stopped being usable is said so plainly, with the way to choose another — never followed on anyone's behalf.
 */
export function JobPrescriptionCard({
  job,
  clinicalUsability,
  onCorrect,
}: JobPrescriptionCardProps) {
  const { t } = useTranslation();
  const rx = job.prescription;
  const clinical = rx?.source === 'CLINICAL_PRESCRIPTION';
  const unusable = clinical && clinicalUsability !== null && clinicalUsability !== 'USABLE';

  const source = !rx
    ? null
    : clinical
      ? t('opticalOrders.job.sourceClinical', { number: rx.clinicalPrescriptionNumber ?? '—' })
      : rx.source === 'EXTERNAL_PRESCRIPTION'
        ? t('opticalOrders.job.sourceExternal')
        : t('opticalOrders.job.sourceLegacy');

  return (
    <Card>
      <CardHeader
        title={t('opticalOrders.steps.prescription')}
        subtitle={source ?? t('opticalOrders.tracking.noPrescription')}
        action={
          onCorrect ? (
            <Button variant="secondary" size="sm" onClick={onCorrect}>
              <RefreshCw className="size-4" aria-hidden />
              {unusable
                ? t('opticalOrders.job.chooseAnother')
                : t('opticalOrders.job.changePrescription')}
            </Button>
          ) : undefined
        }
        wrapAction
      />
      <div className="space-y-3 px-5 py-4">
        {unusable ? (
          <Alert tone="warning">
            {t('opticalOrders.job.rxUnusable', {
              state: t(`sales.opticalJobs.dispensingUsability.${clinicalUsability}`),
            })}
          </Alert>
        ) : null}
        {rx ? (
          <>
            {rx.source === 'EXTERNAL_PRESCRIPTION' && rx.externalReference ? (
              <p className="text-muted text-xs">
                {t('opticalOrders.job.externalReference')}:{' '}
                <span className="text-ink font-mono" dir="ltr">
                  {rx.externalReference}
                </span>
              </p>
            ) : null}
            <ClinicalEyeGrid
              caption={t('opticalOrders.job.gridCaption', { reference: job.reference })}
              value={{ right: eyeFormOf(rx.right), left: eyeFormOf(rx.left) }}
            />
            <PdSummary
              pd={{
                pdType: rx.pdType,
                rightPdMm: rx.rightPdMm,
                leftPdMm: rx.leftPdMm,
                totalPdMm: rx.totalPdMm,
              }}
            />
          </>
        ) : (
          <p className="text-muted text-sm">{t('opticalOrders.tracking.noPrescription')}</p>
        )}
      </div>
    </Card>
  );
}
